import { P3 } from 'components/atoms/P3';
import useWindowSize from 'hooks/useWindowSize';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { animated, useSpring } from 'react-spring';
import styled from 'styled-components';

import { Colors, Spacing, Device, Breakpoints } from 'constants/theme';

export interface TooltipProps {
  children?: React.ReactNode;
  className?: string;
  clickOnly?: boolean;
  copy: string;
  customStyling?: string;
  hide?: boolean;
  onChange?: (isShowing: boolean) => void;
}

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  className,
  clickOnly,
  copy,
  customStyling,
  hide,
  onChange
}) => {
  const [showingTooltip, setShowingTooltip] = useState(false);
  const timeout = useRef<NodeJS.Timeout>();

  const windowSize = useWindowSize();

  const handleTooltipShow = (e) => {
    e.preventDefault();
    setShowingTooltip(true);
    if (onChange) onChange(true);

    if (timeout.current) clearTimeout(timeout.current);

    timeout.current = setTimeout(() => {
      if (!timeout.current || !showingTooltip) return;
      handleTooltipHide();
    }, 3000);
  };

  const handleTooltipHide = useCallback(() => {
    if (timeout.current) clearTimeout(timeout.current);
    setShowingTooltip(false);
    if (onChange) onChange(false);
  }, [onChange]);

  const handleTriggerMouseEnter = (e) => {
    if (windowSize.width < Breakpoints.laptop || clickOnly) return;
    handleTooltipShow(e);
  };

  const handleTriggerMouseLeave = () => {
    if (windowSize.width < Breakpoints.laptop || clickOnly) return;
    handleTooltipHide();
  };

  const fadeStyles = useSpring({
    config: { duration: 500 },
    from: { opacity: 0 },
    to: {
      opacity: showingTooltip ? 1 : 0,
      pointerEvents: showingTooltip ? 'all' : 'none'
    }
  });

  useEffect(() => {
    if (showingTooltip) document.addEventListener('click', handleTooltipHide);
    else document.removeEventListener('click', handleTooltipHide);

    return () => {
      document.removeEventListener('click', handleTooltipHide);
    };
  }, [showingTooltip, handleTooltipHide]);

  useEffect(() => {
    // clearTimeout on unmount
    return () => {
      if (timeout.current) clearTimeout(timeout.current);
    };
  }, []);

  return (
    <Container className={className}>
      <ScreenOverlay
        className="transition-container"
        style={{
          ...fadeStyles,
          zIndex: 1
        }}
      ></ScreenOverlay>
      <TooltipTrigger
        onClick={handleTooltipShow}
        onMouseEnter={handleTriggerMouseEnter}
        onMouseLeave={handleTriggerMouseLeave}
        role="button"
      >
        {children}
      </TooltipTrigger>
      <TooltipCopyWrapper
        className="transition-container"
        customstyling={customStyling}
        hide={hide}
        style={{
          ...fadeStyles,
          position: 'absolute',
          zIndex: 1
        }}
      >
        <P3>{copy}</P3>
      </TooltipCopyWrapper>
    </Container>
  );
};

const ScreenOverlay = styled(animated.div)`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.2);

  @media ${Device.laptop} {
    display: none;
  }
`;

const Container = styled.div`
  position: relative;
`;

const TooltipTrigger = styled.div`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  outline: none;
  font-size: inherit;
  position: relative;
  cursor: pointer;
`;

const TooltipCopyWrapper = styled(animated.div)<{
  customstyling?: string;
  hide?: boolean;
}>`
  position: absolute;
  padding: 15px;
  left: 50%;
  white-space: nowrap;
  background-color: ${Colors.grey1};
  box-shadow: 0px 4px 40px rgba(0, 0, 0, 0.35);
  border-radius: 8px;
  color: ${Colors.grey6};
  max-width: 295px;
  top: 0;
  transform: translateY(-100%) translateX(-50%);
  pointer-events: none;
  display: ${(props) => (props.hide ? 'none' : 'inline')};
  @media ${Device.laptop} {
    left: 100%;
    top: 50%;
    transform: translateY(-50%) translateX(${Spacing.ml});
    pointer-events: all;
  }

  ${(props) => props.customstyling}
`;
